import { useState, useEffect } from 'react';
import { Network } from '@capacitor/network';
import { onlineManager } from '@tanstack/react-query';

export const useNetworkStatus = (): { connected: boolean } => {
  const [connected, setConnected] = useState(true);

  /**
   * Initializes the online status based on the actual network connection
   * when the component mounts. This is crucial because, according to the
   * TanStack documentation, `onlineManager.isOnline()` always starts as
   * true. We need to use the Network plugin to get the true initial connection
   * status and accurately update the `onlineManager` to avoid starting the
   * app in an incorrect "online" state when the device is actually offline.
   * Any subsequent changes in online status (including potential false negatives
   * if the initial check is somehow incorrect) will be handled by the
   * subscription to the onlineManager in the useEffect below.
   */
  useEffect(() => {
    const setInitialConnectionStatus = async (): Promise<void> => {
      const status = await Network.getStatus();
      onlineManager.setOnline(status.connected);
    };

    setInitialConnectionStatus();
  }, []);

  useEffect(() => {
    const unsubscribe = onlineManager.subscribe((isOnline) => {
      setConnected(isOnline);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  return { connected };
};
